import styled, { css } from 'styled-components';
import { Button } from '@upgrade/react-components';
import { Link } from 'gatsby';
import { m, mb, ml, mr, mt, pb, py } from '@upgrade/react-components/Spacing/core';
import CTAButton from './CTAButton';
import Image from './Image';
import * as typographySizes from './TypographyPublicSite/typography-sizes';

const paddingSize = {
  none: '0',
  xs: '1rem 0',
  sm: '2rem 0',
  lg: '5rem 0'
};

const mobilePaddingSize = {
  none: '0',
  xs: '0.5rem 0',
  sm: '1rem 0',
  lg: '2rem 0'
};

export const CoverImage = styled.div`
  background-repeat: no-repeat;
  background-position: 50%;
  background-size: cover;
  @media (min-width: ${props => props.theme.mediaSm}) {
    background-image: url(${({ 'data-image': img }) => img});
  }
`;

export const Hero = styled(CoverImage)`
  position: relative;
  border-bottom: ${({ 'data-border': border = true }) =>
    border ? `1px solid ${props => props.theme.palette.border};` : 'none'};
  background-color: ${props => props.theme.palette[props.backgroundColor] || props.backgroundColor};

  ${({ $verticalPadding = 'lg' }) => py($verticalPadding)}

  @media (min-width: ${props => props.theme.mediaSm}) {
    border: none;
  }
`;

export const MainHero = styled(Hero)`
  ${pb('xl')};
  @media (min-width: ${props => props.theme.mediaSm}) and (max-width: ${props => props.theme.mediaMd}) {
    background: none;
    ${pb('none')};
  }
  @media (min-width: ${props => props.theme.mediaMd}) {
    border: none;
  }
`;

export const MainTitle = styled.h1`
  ${m('none')};
  color: ${({ 'data-color': color = 'inherit', theme }) => theme.palette[color] || color};
  font-size: ${({ theme }) => theme.typography.sizes.headings.mobile.number_lg.fontSize};
  font-weight: ${props => (props.secondary ? 500 : 800)};
  line-height: 3rem;
  @media (min-width: ${props => props.theme.mediaSm}) {
    font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.number_lg.fontSize};
    line-height: 3.56rem;
  }
  @media (min-width: ${props => props.theme.mediaMd}) {
    font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.number_lg.fontSize};
    line-height: 4.56rem;
  }
`;

export const MainSubtitle = styled.h2`
  margin: 1rem 0;
  color: ${({ 'data-color': color = 'inherit', theme }) => theme.palette[color] || color};
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.lg.fontSize};
  font-weight: normal;
  line-height: 1.5rem;
  @media (min-width: ${props => props.theme.mediaSm}) {
    font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.md.fontSize};
    line-height: 2rem;
  }
`;

export const TopSpacer = styled.div`
  margin-top: 6.5rem;
`;

export const Section = styled.div`
  position: relative;
  background-color: ${({ 'data-color': color, theme }) => theme.palette[color] || color};
  color: ${({ 'data-font-color': color, theme }) => theme.palette[color] || color};
  border-bottom: ${({ borderless, theme }) => (borderless ? 'none' : `1px solid ${theme.palette.border};`)};
  padding: ${({ padding = 'lg', theme }) => theme.spacing[padding] || mobilePaddingSize[padding]};

  @media (min-width: ${props => props.theme.mediaSm}) {
    padding: ${({ padding = 'lg' }) => paddingSize[padding]};
  }
`;

export const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.sizes.text.mobile.xl.fontSize};
  line-height: 2rem;
  letter-spacing: 0.2px;
  font-weight: ${({ 'data-font-weight': weight = 500 }) => weight};
  color: ${({ 'data-font-color': color = 'inherit', theme }) => theme.palette[color] || color};
  @media (min-width: ${props => props.theme.mediaSm}) {
    font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.xl.fontSize};
    line-height: 2.625rem;
  }
`;

export const LargeText = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.lg.fontSize};
  ${mb('lg')};

  width: 100%;
  font-weight: ${({ fontWeight }) => fontWeight || '400'};
  @media (min-width: ${props => props.theme.mediaSm}) {
    width: auto;
  }
`;

export const CenteredContainer = styled.div`
  position: relative;
  margin-left: auto;
  margin-right: auto;
`;

export const ConstrainedContent = styled.div`
  max-width: 66.5rem;
  margin-left: auto;
  margin-right: auto;
`;

export const StyledSpan = styled.span`
  background: ${props => props.theme.palette.actionablePrimary};
  border-radius: 10px;
  padding: 5px 10px;
  color: ${props => props.theme.palette.contentReverse};
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.xxs.fontSize};
  vertical-align: top;
  margin-right: 10px;
  text-transform: uppercase;
  font-weight: 500;
`;

export const StyledSecondaryLgBtn = styled(Button)`
  width: 100%;
  @media (min-width: ${props => props.theme.mediaSm}) {
    margin-top: auto;
    max-width: 18.5rem;
  }
`;

const grayArrow = ({ gray }) =>
  gray
    ? ''
    : `
    @media (min-width: ${props => props.theme.mediaSm}) {
      svg path {
        fill: ${props => props.theme.palette.contentReverse};
      }
    }`;

const whiteArrow = ({ white }) =>
  white
    ? `svg path {
      fill:${props => props.theme.palette.contentReverse};
    }`
    : '';

export const ScrollArrow = styled.span`
  position: absolute;
  bottom: 1rem;
  left: 0;
  right: 0;
  a,
  a:hover,
  a:focus {
    text-decoration: none;
    color: ${props => props.theme.palette.contentReverse};
    border: 0;
    outline: 0;
  }
  ${grayArrow};
  ${whiteArrow};
`;

export const ScrollArrowInverted = styled(ScrollArrow)`
  bottom: 0;
  transform: rotate(180deg);
`;

export const UnderlinedLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.theme.palette.content};
  font-weight: 500;
  &:hover {
    text-decoration: inherit;
    color: inherit;
  }
  border-bottom: 1px solid ${props => props.theme.palette.semanticInfoStrong};
`;

export const TextLarge = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.lg.fontSize};
  ${mb('lg')};
`;

export const AccessibilityPageStyle = css`
  padding: 0 !important;
  a,
  a:hover,
  a:focus {
    text-decoration: none !important;
  }
  .indent-1 {
    ${ml('xl')};
  }
`;

export const SmsPageStyle = css`
  a,
  a:hover,
  a:focus {
    text-decoration: none !important;
  }
`;

export const StyledLineHeader = styled.div`
  text-align: ${({ $textAlign }) => $textAlign || 'start'};
  text-transform: uppercase;
  color: ${({ theme, 'data-color': color = 'content' }) => theme.palette[color]};
  position: relative;
  overflow: hidden;
  font-weight: 500;
  letter-spacing: 1px;

  ${typographySizes.text.mobile.xxs}

  @media (min-width: ${props => props.theme.mediaSm}) {
    letter-spacing: 1.2px;
    ${typographySizes.text.desktop.xxs}
  }
`;

export const MainSubtitleBold = styled(MainSubtitle)`
  font-weight: 500;
`;

export const StyledCTAButton = styled(CTAButton)`
  border-radius: 10px;
  a {
    border-radius: 10px;
  }
`;

export const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.theme.palette.actionablePrimary};
  font-weight: 500;
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.md.fontSize};
  border-bottom: 2px solid ${props => props.theme.palette.actionablePrimary};
  padding: 0.5rem 0;
  ${mr('sm')};

  &:hover,
  &:focus {
    text-decoration: none;
    color: ${props => props.theme.palette.actionablePrimary};
  }
`;

export const UndecoratedLink = styled(Link)`
  &,
  &:hover {
    text-decoration: none;
  }
`;

export const FdicText = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  ${mt('sm')};
  text-align: left;
`;

export const FdicIcon = styled.div`
  ${mr('sm')};
`;

export const ResponsiveImage = styled(Image)`
  max-width: 100%;
  height: auto;
`;
